import React from 'react';
import {
  Drawer,
  TextField,
  Button,
  Box,
  Grid,
  Typography,
  CircularProgress,
  Autocomplete,
  Divider,
  IconButton,
} from '@mui/material';
import { getAllProjects, Project } from '../../services/projectService';
import { createWarehouse } from '../../services/warehouseService';
import { useNotification } from '../layouts/Layout';

interface AddWarehouseDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const textFieldSx = {
  '& .MuiInputLabel-root': {
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '16px',
    color: '#2D2D32',
  },
  '& .MuiOutlinedInput-root': {
    height: 32,
    backgroundColor: '#fff',
    borderRadius: '4px',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2020AF !important' },
};

const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
    {text}
    {required && <Box component="span" sx={{ color: '#d32f2f' }}> *</Box>}
  </Typography>
);

const AddWarehouseDialog: React.FC<AddWarehouseDialogProps> = ({ open, onClose, onSaved }) => {
  const { showNotification } = useNotification();
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({
    warehouse_code: '',
    warehouse_name: '',
    project_id: '',
  });
  const [orderingParty, setOrderingParty] = React.useState('');
  const [address, setAddress] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setForm({ warehouse_code: '', warehouse_name: '', project_id: '' });
      setOrderingParty('');
      setAddress('');
      setLoadingProjects(true);
      getAllProjects()
        .then((p) => setProjects(p || []))
        .catch(() => setProjects([]))
        .finally(() => setLoadingProjects(false));
    }
  }, [open]);

  const selectedProject = projects.find((p) => p.project_id === form.project_id);

  React.useEffect(() => {
    if (selectedProject) {
      setOrderingParty(selectedProject.client_name || '');
      setAddress(selectedProject.investment_address || '');
    } else {
      setOrderingParty('');
      setAddress('');
    }
  }, [selectedProject]);

  const handleSave = async () => {
    if (!form.warehouse_code.trim() || !form.warehouse_name.trim()) {
      showNotification('Wypełnij kod i nazwę magazynu', 'error');
      return;
    }
    setSaving(true);
    try {
      await createWarehouse({
        warehouse_code: form.warehouse_code.trim(),
        warehouse_name: form.warehouse_name.trim(),
        project_id: form.project_id || undefined,
      });
      showNotification('Magazyn został dodany', 'success');
      onSaved?.();
      onClose();
    } catch (err: any) {
      showNotification(err?.response?.data?.message || 'Błąd zapisu magazynu', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
            Dodaj magazyn
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 30,
              minHeight: 30,
              '& > *': { pointerEvents: 'none' },
            }}
          >
            <Box
              component="img"
              src="/assets/close.svg"
              alt="Zamknij"
              sx={{
                width: 13,
                height: 13,
                display: 'block',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
              }}
            />
          </IconButton>
        </Box>
        <Box sx={{ px: 4, mt: '36px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '16px', color: '#020242', mb: 0 }}>
            Dane podstawowe
          </Typography>
        </Box>
        <Box sx={{ p: 4, pt: 3, flex: 1, overflowY: 'auto' }}>
          <Grid container rowSpacing={1.5} columnSpacing={3}>
            <Grid item xs={12} md={6}>
              <FieldLabel text="Kod magazynu" required />
              <TextField
                fullWidth
                value={form.warehouse_code}
                onChange={(e) => setForm((p) => ({ ...p, warehouse_code: e.target.value.slice(0, 50) }))}
                placeholder="np. 080"
                variant="outlined"
                size="small"
                sx={textFieldSx}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FieldLabel text="Nazwa magazynu" required />
              <TextField
                fullWidth
                value={form.warehouse_name}
                onChange={(e) => setForm((p) => ({ ...p, warehouse_name: e.target.value }))}
                placeholder="np. Magazyn budowy"
                variant="outlined"
                size="small"
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldLabel text="Projekt" />
              <Autocomplete
                size="small"
                options={projects}
                getOptionLabel={(p) => `${p.project_id} ${p.project_name}`}
                value={selectedProject || null}
                onChange={(_, val) => setForm((p) => ({ ...p, project_id: val ? val.project_id : '' }))}
                loading={loadingProjects}
                isOptionEqualToValue={(a, b) => a.project_id === b.project_id}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Wybierz projekt" variant="outlined" sx={textFieldSx} />
                )}
              />
            </Grid>
            {form.project_id && (
              <>
                <Grid item xs={12}>
                  <FieldLabel text="Zamawiający" />
                  <TextField
                    fullWidth
                    value={orderingParty}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                    sx={{
                      ...textFieldSx,
                      '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel text="Adres projektu" />
                  <TextField
                    fullWidth
                    value={address}
                    InputProps={{ readOnly: true }}
                    multiline
                    rows={2}
                    variant="outlined"
                    size="small"
                    sx={{
                      ...textFieldSx,
                      '& .MuiOutlinedInput-root': { height: 'auto', minHeight: 56, backgroundColor: '#f5f5f5', alignItems: 'flex-start' },
                      '& .MuiInputBase-input': { padding: 0 },
                      '& .MuiOutlinedInput-input': { padding: '8.5px 14px' },
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>
        <Divider />
        <Box sx={{ px: 4, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, boxShadow: '0px 4px 4px rgba(0,0,0,0.05)' }}>
          <Button onClick={onClose} variant="text" disabled={saving} sx={{ fontFamily: 'Roboto, sans-serif', textTransform: 'none' }}>
            Anuluj
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              fontFamily: 'Roboto, sans-serif',
              textTransform: 'none',
              backgroundColor: '#020242',
              '&:hover': { backgroundColor: '#030360' },
            }}
          >
            {saving ? <CircularProgress size={24} color="inherit" /> : 'Zapisz'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default AddWarehouseDialog;
